/**
 * The Admin's "Endereços" screen (D674, doc 27 section 4; owner, 2026-10-22) - framework-neutral view-models over `/v1/admin/places`: the shared base of addresses and
 * named places, shown the way the owner's DevBase "Lista de Endereços" does (a filter under every column, sortable headers) plus manual create / edit / hide / delete
 * and the bulk clears.
 *
 *  - the authority (`places:view` / `places:manage` / `places:purge`), so a button is drawn only for a word the operator holds;
 *  - the COLUMNS a scope shows: the top scope selector is the ONE geography control, so a column the scope already pins is not drawn (the country column only at the
 *    global scope, the state column only at the global and country scopes) and there is no second country select;
 *  - the filters and the sort -> the query the server takes (dates are calendar days on the market's own clock, converted to UTC instants);
 *  - the form draft of a place (texts as typed, coordinates as text) with the checks the server also applies, and the minimal patch of an edit;
 *  - the bulk clears: what each choice means in the current scope, the request it sends and the query that counts the rows it will remove.
 *
 * The console only presents: the server authorizes every call at the geography it names and validates every value again. Neutral: no country, city, postal format or
 * language is named here.
 */
import type { AdminPlaceInput, AdminPlacePatch, AdminPlacePurgeInput, AdminPlaceSortColumn, AdminPlaceView, AdminPlacesQuery, AdminPlacesRetention, OperatingMarketView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { marketDayEnd, marketDayStart, marketTimeZone, scopeTimeZone } from './market-time.js';
import type { OperatorScope } from './shell.js';

// ---- authority ---------------------------------------------------------------------------------------------------------------------------------------

export interface PlacesAuthority {
  readonly mayView: boolean;
  readonly mayManage: boolean;
  /** The bulk clears and the route-cache clear (the admin roles). */
  readonly mayPurge: boolean;
}

export function placesAuthority(grants: KnownGrants): PlacesAuthority {
  if (grants === undefined) return { mayView: false, mayManage: false, mayPurge: false };
  return {
    mayView: grantsAllow(grants, { permission: 'places:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'places:manage', target: 'GEO' }),
    mayPurge: grantsAllow(grants, { permission: 'places:purge', target: 'GEO' }),
  };
}

// ---- columns -----------------------------------------------------------------------------------------------------------------------------------------

export const PLACE_COLUMN_KEYS = ['id', 'createdAt', 'postalCode', 'latitude', 'longitude', 'name', 'address', 'city', 'state', 'country', 'kind', 'source', 'useCount', 'verified', 'status'] as const;
export type PlaceColumnKey = (typeof PLACE_COLUMN_KEYS)[number];

/** The server's sort column for a table column; a column with none (the id, the verified mark) is not sortable. */
export const PLACE_SORT_OF: Readonly<Partial<Record<PlaceColumnKey, AdminPlaceSortColumn>>> = {
  createdAt: 'createdAt',
  postalCode: 'postalCode',
  latitude: 'latitude',
  longitude: 'longitude',
  name: 'name',
  address: 'formattedAddress',
  city: 'cityName',
  state: 'regionCode',
  country: 'countryCode',
  kind: 'kind',
  source: 'source',
  useCount: 'useCount',
};

/**
 * The columns a scope shows, in order. The scope selector at the top is the only geography control (owner feedback): the country is shown only when the scope names no
 * country (the global scope), and the state only when it names neither a state nor a city (global and country scopes).
 */
export function placesVisibleColumns(scope: OperatorScope): readonly PlaceColumnKey[] {
  const countryPinned = scope.country !== undefined && scope.country !== '';
  const statePinned = (scope.region !== undefined && scope.region !== '') || (scope.city !== undefined && scope.city !== '');
  return PLACE_COLUMN_KEYS.filter((k) => (k === 'country' ? !countryPinned : k === 'state' ? !statePinned : true));
}

// ---- filters, sort, query ------------------------------------------------------------------------------------------------------------------------------

export interface PlacesFilters {
  readonly id: string;
  /** `YYYY-MM-DD` on the market's own clock. */
  readonly createdFrom: string;
  readonly createdTo: string;
  readonly postalCode: string;
  /** The address text: contained in the name, aliases, address line, neighborhood, city or postal code. */
  readonly q: string;
  readonly city: string;
  /** A state code (the select), or empty. Only offered where the scope does not pin the state. */
  readonly region: string;
  /** A country code (the select), or empty. Only offered at the global scope. */
  readonly country: string;
  readonly source: string;
  readonly kind: string;
  readonly verified: '' | 'true' | 'false';
  /** The situation: shown in the base (not hidden, not expired), hidden by an operator, or past its expiry. */
  readonly status: '' | PlaceStatus;
}

export type PlaceStatus = 'visible' | 'hidden' | 'expired';
export const PLACE_STATUSES: readonly PlaceStatus[] = ['visible', 'hidden', 'expired'];

/** A place's situation in one word: hidden wins over expired (an operator's decision), then expired, else visible. */
export const placeStatusOf = (p: Pick<AdminPlaceView, 'hidden' | 'expired'>): PlaceStatus => (p.hidden ? 'hidden' : p.expired ? 'expired' : 'visible');

export const EMPTY_PLACES_FILTERS: PlacesFilters = { id: '', createdFrom: '', createdTo: '', postalCode: '', q: '', city: '', region: '', country: '', source: '', kind: '', verified: '', status: '' };

export const placesFiltersActive = (f: PlacesFilters): boolean => (Object.keys(f) as (keyof PlacesFilters)[]).some((k) => f[k] !== '');

export interface PlacesSort {
  readonly column: AdminPlaceSortColumn;
  readonly direction: 'asc' | 'desc';
}

/** Newest first, like the DevBase list. */
export const DEFAULT_PLACES_SORT: PlacesSort = { column: 'createdAt', direction: 'desc' };

/** Click on a sortable header: another column sorts ascending; the sorted column flips. */
export function toggledSort(sort: PlacesSort, column: AdminPlaceSortColumn): PlacesSort {
  return sort.column === column ? { column, direction: sort.direction === 'asc' ? 'desc' : 'asc' } : { column, direction: 'asc' };
}

export const PLACES_PAGE_SIZE = 50;

/** The scope the top selector names, as the query's `country` / `region` / `market` (none = the platform). A city carries its country; a state its own prefix. */
export function placesScopeQuery(scope: OperatorScope): Pick<AdminPlacesQuery, 'country' | 'region' | 'market'> {
  if (scope.city !== undefined && scope.city !== '') return { market: scope.city };
  if (scope.region !== undefined && scope.region !== '') return { region: scope.region };
  if (scope.country !== undefined && scope.country !== '') return { country: scope.country };
  return {};
}

/**
 * The timezone the Cadastro filter's days are read in: the scope's own (a city's, or the one every market of the state / country shares), else the timezone of the first
 * registered market of the scope (the global scope has none: UTC).
 */
export function placesFilterTimeZone(scope: OperatorScope, markets: readonly OperatingMarketView[]): string | undefined {
  return scopeTimeZone(scope, markets);
}

/** The list query for a scope, the column filters, the sort and the page. A filter the scope already pins is ignored (the selector at the top is the one control). */
export function placesQuery(scope: OperatorScope, filters: PlacesFilters, sort: PlacesSort, offset: number, timeZone: string | undefined, limit: number = PLACES_PAGE_SIZE): AdminPlacesQuery {
  const base = placesScopeQuery(scope);
  const narrowed: Pick<AdminPlacesQuery, 'country' | 'region' | 'market'> =
    base.market !== undefined || base.region !== undefined ? base
    : filters.region !== '' ? { region: filters.region }
    : base.country !== undefined ? base
    : filters.country !== '' ? { country: filters.country }
    : base;
  const from = filters.createdFrom === '' ? undefined : marketDayStart(filters.createdFrom, timeZone);
  const to = filters.createdTo === '' ? undefined : marketDayEnd(filters.createdTo, timeZone);
  const flag = (v: '' | 'true' | 'false'): boolean | undefined => (v === '' ? undefined : v === 'true');
  const text = (v: string): string | undefined => (v.trim() === '' ? undefined : v.trim());
  return {
    ...narrowed,
    ...(text(filters.id) !== undefined ? { id: text(filters.id) as string } : {}),
    ...(from !== undefined ? { createdFrom: from } : {}),
    ...(to !== undefined ? { createdTo: to } : {}),
    ...(text(filters.postalCode) !== undefined ? { postalCode: text(filters.postalCode) as string } : {}),
    ...(text(filters.q) !== undefined ? { q: text(filters.q) as string } : {}),
    ...(text(filters.city) !== undefined ? { city: text(filters.city) as string } : {}),
    ...(filters.source !== '' ? { source: filters.source } : {}),
    ...(filters.kind !== '' ? { kind: filters.kind } : {}),
    ...(flag(filters.verified) !== undefined ? { verified: flag(filters.verified) as boolean } : {}),
    ...(filters.status === 'visible' ? { hidden: false, expired: false } : filters.status === 'hidden' ? { hidden: true } : filters.status === 'expired' ? { expired: true, hidden: false } : {}),
    sort: sort.column,
    direction: sort.direction,
    limit,
    offset,
  };
}

// ---- cells -------------------------------------------------------------------------------------------------------------------------------------------

/** The first eight characters of an id: the table reads a short, copyable code, never the whole uuid. */
export const shortPlaceId = (id: string): string => id.slice(0, 8);

/** A coordinate as the table shows it: six decimals (about 0.1 m), the way it is stored. */
export const formatCoordinate = (n: number): string => n.toFixed(6);

/** The timezone a row's instant is shown in: its own market's, else the scope's, else none (the formatter then says UTC). */
export function placeTimeZone(place: Pick<AdminPlaceView, 'marketId'>, scopeZone: string | undefined, markets: readonly OperatingMarketView[]): string | undefined {
  return (place.marketId !== null ? marketTimeZone(place.marketId, markets) : undefined) ?? scopeZone;
}

/** What the retention reads: no limit (the default), or a number of days, with the sources that follow their own rule. */
export interface RetentionText {
  readonly days: number | null;
  readonly overrides: readonly { readonly source: string; readonly days: number | null }[];
}

export function retentionText(retention: AdminPlacesRetention): RetentionText {
  return { days: retention.days, overrides: Object.entries(retention.bySource).map(([source, days]) => ({ source, days })) };
}

// ---- the form ----------------------------------------------------------------------------------------------------------------------------------------

export interface PlaceDraft {
  readonly countryCode: string;
  readonly regionCode: string;
  readonly marketId: string;
  readonly cityName: string;
  readonly postalCode: string;
  readonly name: string;
  readonly aliases: readonly string[];
  readonly street: string;
  readonly houseNumber: string;
  readonly neighborhood: string;
  readonly complement: string;
  readonly formattedAddress: string;
  readonly kind: string;
  readonly precision: string;
  /** The point on the map, as typed (decimal degrees). */
  readonly latitude: string;
  readonly longitude: string;
  readonly pickupLatitude: string;
  readonly pickupLongitude: string;
}

export const EMPTY_PLACE_DRAFT: PlaceDraft = {
  countryCode: '', regionCode: '', marketId: '', cityName: '', postalCode: '', name: '', aliases: [], street: '', houseNumber: '', neighborhood: '', complement: '', formattedAddress: '', kind: '', precision: 'rooftop',
  latitude: '', longitude: '', pickupLatitude: '', pickupLongitude: '',
};

/** A new place starts in the geography the scope names: the city (with its state and country), the state, or the country. */
export function newPlaceDraft(scope: OperatorScope, markets: readonly OperatingMarketView[]): PlaceDraft {
  const city = scope.city !== undefined && scope.city !== '' ? markets.find((m) => m.id === scope.city) : undefined;
  const country = scope.country ?? city?.countryCode ?? (scope.region !== undefined ? scope.region.slice(0, 2) : '');
  return {
    ...EMPTY_PLACE_DRAFT,
    countryCode: country.toUpperCase(),
    regionCode: city?.regionCode ?? scope.region ?? '',
    marketId: city?.id ?? '',
    cityName: city?.name ?? '',
  };
}

const tidy = (v: string): string => v.replace(/\s+/g, ' ').trim();
const num = (n: number): string => String(Math.round(n * 1e6) / 1e6);

export function draftOfPlace(p: AdminPlaceView): PlaceDraft {
  return {
    countryCode: p.countryCode,
    regionCode: p.regionCode ?? '',
    marketId: p.marketId ?? '',
    cityName: p.cityName ?? '',
    postalCode: p.postalCode ?? '',
    name: p.name ?? '',
    aliases: p.aliases,
    street: p.street ?? '',
    houseNumber: p.houseNumber ?? '',
    neighborhood: p.neighborhood ?? '',
    complement: p.complement ?? '',
    formattedAddress: p.formattedAddress,
    kind: p.kind ?? '',
    precision: p.precision,
    latitude: num(p.location.latitude),
    longitude: num(p.location.longitude),
    pickupLatitude: p.pickupPoint === null ? '' : num(p.pickupPoint.latitude),
    pickupLongitude: p.pickupPoint === null ? '' : num(p.pickupPoint.longitude),
  };
}

/** A typed coordinate (a comma or a point as the decimal mark), or undefined when it is not a number. */
export function parsePlaceCoordinate(text: string): number | undefined {
  const t = text.trim().replace(',', '.');
  if (!/^-?\d{1,3}(\.\d+)?$/.test(t)) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

type Point = { readonly latitude: number; readonly longitude: number };

export function draftPoint(lat: string, lng: string): Point | undefined {
  const latitude = parsePlaceCoordinate(lat);
  const longitude = parsePlaceCoordinate(lng);
  return latitude !== undefined && longitude !== undefined && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180 ? { latitude, longitude } : undefined;
}

/** The pickup point: none when both fields are empty, undefined (invalid) when only one is filled or the pair is not a point. */
export function draftPickup(d: PlaceDraft): Point | null | undefined {
  if (d.pickupLatitude.trim() === '' && d.pickupLongitude.trim() === '') return null;
  return draftPoint(d.pickupLatitude, d.pickupLongitude);
}

/** The first thing wrong with a draft, as the stable `PLACE_*` reason code the catalog words (`problem.place_admin`), or undefined when it can be sent. The server checks again. */
export function placeDraftIssue(d: PlaceDraft): string | undefined {
  if (!/^[A-Z]{2}$/.test(d.countryCode)) return 'PLACE_COUNTRY_INVALID';
  if (tidy(d.formattedAddress) === '' || tidy(d.formattedAddress).length > 400) return 'PLACE_ADDRESS_INVALID';
  if (draftPoint(d.latitude, d.longitude) === undefined) return 'PLACE_LOCATION_INVALID';
  if (draftPickup(d) === undefined) return 'PLACE_PICKUP_POINT_INVALID';
  if (d.marketId !== '' && d.marketId.slice(0, 2).toUpperCase() !== d.countryCode) return 'PLACE_MARKET_COUNTRY_MISMATCH';
  if (d.regionCode !== '' && d.regionCode.slice(0, 2).toUpperCase() !== d.countryCode) return 'PLACE_REGION_COUNTRY_MISMATCH';
  const texts = [d.cityName, d.postalCode, d.name, d.street, d.houseNumber, d.neighborhood, d.complement];
  if (texts.some((t) => tidy(t).length > 200)) return 'PLACE_TEXT_FIELD_INVALID';
  if (d.aliases.length > 20 || d.aliases.some((a) => tidy(a) === '' || tidy(a).length > 200)) return 'PLACE_ALIASES_INVALID';
  return undefined;
}

const orNull = (v: string): string | null => (tidy(v) === '' ? null : tidy(v));

/** The create body of a valid draft; undefined when the draft has an issue. */
export function placeInputOf(d: PlaceDraft): AdminPlaceInput | undefined {
  if (placeDraftIssue(d) !== undefined) return undefined;
  const location = draftPoint(d.latitude, d.longitude) as Point;
  const pickup = draftPickup(d) as Point | null;
  return {
    countryCode: d.countryCode,
    regionCode: orNull(d.regionCode),
    marketId: orNull(d.marketId),
    cityName: orNull(d.cityName),
    postalCode: orNull(d.postalCode),
    location,
    pickupPoint: pickup,
    name: orNull(d.name),
    aliases: d.aliases.map(tidy).filter((a) => a !== ''),
    street: orNull(d.street),
    houseNumber: orNull(d.houseNumber),
    neighborhood: orNull(d.neighborhood),
    complement: orNull(d.complement),
    formattedAddress: tidy(d.formattedAddress),
    kind: orNull(d.kind),
    precision: d.precision as AdminPlaceInput['precision'] & string,
  };
}

const samePoint = (a: Point | null, b: Point | null): boolean => (a === null || b === null ? a === b : Math.abs(a.latitude - b.latitude) < 1e-7 && Math.abs(a.longitude - b.longitude) < 1e-7);

/** The edit that turns a place into a draft: only the members that differ. Undefined when the draft has an issue; `{}` when nothing changed. */
export function placePatchOf(place: AdminPlaceView, d: PlaceDraft): AdminPlacePatch | undefined {
  const input = placeInputOf(d);
  if (input === undefined) return undefined;
  const patch: Record<string, unknown> = {};
  const text = ['regionCode', 'marketId', 'cityName', 'postalCode', 'name', 'street', 'houseNumber', 'neighborhood', 'complement', 'kind'] as const;
  for (const k of text) if ((input[k] ?? null) !== place[k]) patch[k] = input[k] ?? null;
  if (input.formattedAddress !== place.formattedAddress) patch.formattedAddress = input.formattedAddress;
  if (input.precision !== undefined && input.precision !== place.precision) patch.precision = input.precision;
  if (!samePoint(input.location, place.location)) patch.location = input.location;
  if (!samePoint(input.pickupPoint ?? null, place.pickupPoint)) patch.pickupPoint = input.pickupPoint ?? null;
  const aliases = input.aliases ?? [];
  if (aliases.length !== place.aliases.length || aliases.some((a, i) => a !== place.aliases[i])) patch.aliases = aliases;
  return patch as AdminPlacePatch;
}

/** Add an alias (chip); empty and repeated ones (by their folded form) are ignored. */
export function withAlias(d: PlaceDraft, alias: string): PlaceDraft {
  const a = tidy(alias);
  const key = a.normalize('NFKD').replace(/\p{M}+/gu, '').toLowerCase();
  const seen = new Set(d.aliases.map((x) => x.normalize('NFKD').replace(/\p{M}+/gu, '').toLowerCase()));
  if (a === '' || seen.has(key)) return d;
  return { ...d, aliases: [...d.aliases, a] };
}

export const withoutAlias = (d: PlaceDraft, index: number): PlaceDraft => ({ ...d, aliases: d.aliases.filter((_, i) => i !== index) });

/** Which of the two points a tap on the map places. */
export type PlaceMapTarget = 'point' | 'pickup';

export function withMapTap(d: PlaceDraft, target: PlaceMapTarget, p: Point): PlaceDraft {
  const lat = num(p.latitude);
  const lng = num(p.longitude);
  return target === 'point' ? { ...d, latitude: lat, longitude: lng } : { ...d, pickupLatitude: lat, pickupLongitude: lng };
}

// ---- the bulk clears -----------------------------------------------------------------------------------------------------------------------------------

/** What to clear in the scope the top selector names: all of it, only one source, or only the expired. */
export type PurgeChoice = 'all' | 'source' | 'expired';
export const PURGE_CHOICES: readonly PurgeChoice[] = ['all', 'source', 'expired'];

export interface PurgeOptions {
  readonly choice: PurgeChoice;
  readonly source: string;
  readonly keepVerified: boolean;
}

/** Whether the choice needs a source picked before it can be asked. */
export const purgeNeedsSource = (choice: PurgeChoice): boolean => choice === 'source';

/** The word typed to confirm clearing everything of the scope (the catalog supplies the language's word). */
export const purgeNeedsWord = (choice: PurgeChoice): boolean => choice === 'all';

/**
 * The request a choice sends, narrowed to the scope: a city (mode `market` for "all", a narrowing for the others), else a state, else a country, else the platform. The
 * server authorizes at that narrowing.
 */
export function purgeRequestOf(scope: OperatorScope, options: PurgeOptions): AdminPlacePurgeInput | undefined {
  const city = scope.city !== undefined && scope.city !== '' ? scope.city : undefined;
  const region = city === undefined && scope.region !== undefined && scope.region !== '' ? scope.region : undefined;
  const country = city === undefined && region === undefined && scope.country !== undefined && scope.country !== '' ? scope.country : undefined;
  if (options.choice === 'source' && options.source === '') return undefined;
  const mode = options.choice === 'all' ? (city !== undefined ? 'market' : 'all') : options.choice;
  return {
    mode,
    ...(city !== undefined ? { marketId: city } : {}),
    ...(region !== undefined ? { regionCode: region } : {}),
    ...(country !== undefined ? { countryCode: country } : {}),
    ...(options.choice === 'source' ? { source: options.source } : {}),
    keepVerified: options.keepVerified,
  };
}

/** The list query that counts the rows a clear will remove ("this will remove N places"). */
export function purgeCountQuery(scope: OperatorScope, options: PurgeOptions): AdminPlacesQuery {
  return {
    ...placesScopeQuery(scope),
    ...(options.choice === 'source' && options.source !== '' ? { source: options.source } : {}),
    ...(options.choice === 'expired' ? { expired: true } : {}),
    ...(options.keepVerified ? { verified: false } : {}),
    limit: 1,
    offset: 0,
  };
}

/** The route-cache clear is the platform's: it is offered only at the global scope. */
export const mayClearRouteCache = (scope: OperatorScope, authority: PlacesAuthority): boolean =>
  authority.mayPurge && (scope.country === undefined || scope.country === '') && (scope.region === undefined || scope.region === '') && (scope.city === undefined || scope.city === '');
